import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useStyles, useSettings } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { TabState } from '../../../../constants/settings-events';
import { withClientSideRenderMark } from '../../../../contexts/ClientSideRenderMark';
import { useSettingsParams } from '../../../../hooks';
import { useShouldUseMobileSettings } from '../../../../hooks/useShouldUseMobileSettings';
import { Alignment, alignmentName } from '../../../../utils/alignment';
import { safeParse } from '../../../../utils/common';
import { getPlanListEntriesForTab } from '../../../../utils/tabs';
import stylesParams from '../../stylesParams';
import { TabSubtitlePosition } from '../../tabsStylesParams';
import { ListWidgetProps } from '../List';
import { Disclaimer } from '../List/Disclaimer';
import { EmptyState } from '../List/EmptyState';
import { ErrorState } from '../List/ErrorState';
import { Header } from '../List/Header';
import { st as listSt, classes as listClasses } from '../List/List.st.css';
import { MAX_ITEM_WIDTH, PlanList } from '../List/PlanList';
import { classes, st } from './TabsView.st.css';

const TabsView: React.FC<ListWidgetProps> = ({
  plans,
  tabState,
  buttonDemo,
  areSettingsOpened,
  selectPlan,
  addToCart,
  resetLoadingState,
  dataHook,
  emptyState,
  selectedPlanId,
  isOnEcom,
  isAddToCartLoading,
  plansInTabsView,
  tabSubtitles = {},
  variantCategoryNameById = {},
  plansErrorMessage,
  retryLoadPlans,
  isRetryLoading,
  overrideAppTitle,
  overrideAppSubtitle,
}) => {
  const styles = useStyles();
  const { isMobile, isEditorX } = useEnvironment();
  const useMobileSettings = useShouldUseMobileSettings();

  const tabsAlignment = alignmentName(styles.get(stylesParams.tabsAlignment) as Alignment);
  const isSubtitleOnNewLine = styles.get(stylesParams.tabSubtitlePosition) === TabSubtitlePosition.NEW_LINE;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const settings = useSettings();
  const settingsParams = useSettingsParams();

  // Only categories that resolve to a known category render as tabs — a missing/deleted category is not a tab.
  const selectedCategoryIds = useMemo(
    () =>
      safeParse<string[]>(settings.get(settingsParams.visibleTabs), []).filter(
        (categoryId) => variantCategoryNameById[categoryId],
      ),
    [settings, settingsParams.visibleTabs, variantCategoryNameById],
  );

  useEffect(() => {
    if (activeTabIndex >= selectedCategoryIds.length) {
      setActiveTabIndex(0);
    }
  }, [selectedCategoryIds.length, activeTabIndex]);

  const activeCategoryId = selectedCategoryIds[activeTabIndex] ?? '';

  const { plans: tabPlans, variantEntries } = useMemo(
    () =>
      getPlanListEntriesForTab({
        plans: plans as Plan[],
        activeCategoryId,
        plansInTabsView: plansInTabsView ?? [],
        selectedCategoryIds,
        variantCategoryNameById,
      }),
    [plans, activeCategoryId, plansInTabsView, selectedCategoryIds, variantCategoryNameById],
  );

  const extraPlanListPadding = 30;
  const variantEntryCount = Object.values(variantEntries ?? {}).flat().length;
  const entryCount = variantEntryCount > 0 ? variantEntryCount : tabPlans.length;
  const maxWidthStyle =
    !isEditorX && entryCount ? { maxWidth: MAX_ITEM_WIDTH * entryCount - extraPlanListPadding } : {};

  if (plansErrorMessage) {
    return <ErrorState onRetry={retryLoadPlans} isLoading={isRetryLoading} />;
  }

  if (selectedCategoryIds.length === 0) {
    return <EmptyState {...emptyState} />;
  }

  return (
    <div
      className={listSt(listClasses.root, { mobile: !!isMobile, useMobileSettings })}
      data-hook={dataHook ?? VIEWER_HOOKS.PLAN_LIST_TABS_VIEW}
    >
      <Header
        overrideAppTitle={overrideAppTitle}
        overrideAppSubtitle={overrideAppSubtitle}
        maxWidthStyle={maxWidthStyle}
      />
      <div className={st(classes.tabsStrip, { alignment: tabsAlignment })}>
        <div role="tablist" data-hook={VIEWER_HOOKS.PLAN_LIST_TABS_STRIP} className={classes.tabsGroup}>
          <span aria-hidden="true" className={classes.activeTabPill} />
          {selectedCategoryIds.map((categoryId, index) => {
            const isActive = index === activeTabIndex;
            const subtitle = get(tabSubtitles, [categoryId]);

            return (
              <button
                type="button"
                className={st(classes.tab, { subtitleOnNewLine: isSubtitleOnNewLine })}
                key={categoryId}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTabIndex(index)}
                style={isActive ? ({ anchorName: '--active-tab' } as React.CSSProperties) : undefined}
              >
                <span className={classes.tabTitle}>{variantCategoryNameById[categoryId]}</span>
                {subtitle && (
                  <span className={classes.tabSubtitle} data-hook={VIEWER_HOOKS.PLAN_LIST_TAB_SUBTITLE}>
                    {subtitle}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {tabPlans.length === 0 ? (
        <EmptyState {...emptyState} />
      ) : (
        <PlanList
          plans={tabPlans}
          variantEntries={variantEntries}
          variantCategoryNameById={variantCategoryNameById}
          isVariantSelectionEnabled
          isTabsView
          buttonDemo={buttonDemo}
          showDemoHighlight={tabState === TabState.HIGHLIGHTED}
          expandMobileBenefits={areSettingsOpened}
          selectPlan={selectPlan}
          addToCart={addToCart}
          resetLoadingState={resetLoadingState}
          selectedPlanId={selectedPlanId}
          isOnEcom={isOnEcom}
          isAddToCartLoading={isAddToCartLoading}
        />
      )}
      <Disclaimer maxWidthStyle={maxWidthStyle} />
    </div>
  );
};

export default withClientSideRenderMark(TabsView);
